import React, { useState, useMemo } from 'react';
import { useGame } from '../../context/GameContext';
import { resources, technologies, BALANCE } from '../../data';
import { ResourceChip } from '../common';
import TechChip from '../common/TechChip';
import { canAfford as checkCanAfford } from '../../modules/shared';
import { getAdvisorTotalsForDisplay } from '../../modules/advisors';

// Tier names for flavor
const TIER_NAMES = {
  1: 'Foundations',
  2: 'Early Development',
  3: 'Advancement',
  4: 'Specialization',
  5: 'Expansion',
  6: 'Industry',
  7: 'Commerce',
  8: 'Innovation',
  9: 'Mastery',
  10: 'Legendary',
  11: 'Transcendence'
};

function AdvisorsModal({ onClose, onNotification }) {
  const {
    playerResources,
    researchedTechs,
    unlockedAdvisors,
    activeAdvisors,
    unlockAdvisor,
    activateAdvisor,
    deactivateAdvisor,
    advisorSlots,
    purchaseAdvisorSlot,
  } = useGame();

  // Pre-select the first active advisor, or first unlocked, or first available advisor
  const [selectedAdvisor, setSelectedAdvisor] = useState(() => {
    // Priority: active > unlocked > available > first
    const activeAdvisor = BALANCE.advisors.find(a => activeAdvisors.includes(a.id));
    if (activeAdvisor) return activeAdvisor;

    const unlockedAdvisor = BALANCE.advisors.find(a => unlockedAdvisors.includes(a.id));
    if (unlockedAdvisor) return unlockedAdvisor;

    const availableAdvisor = BALANCE.advisors.find(a => !a.unlockTech || researchedTechs.includes(a.unlockTech));
    if (availableAdvisor) return availableAdvisor;

    return BALANCE.advisors[0] || null;
  });
  const [filterTier, setFilterTier] = useState('all');

  const maxSlots = advisorSlots || BALANCE.baseAdvisorSlots || 2;

  // Generate tooltip text for an advisor
  const getAdvisorTooltip = (advisor) => {
    const lines = [advisor.name + ' - ' + advisor.title];
    
    Object.entries(advisor.bonuses || {}).forEach(([k, v]) => {
      lines.push(`+${Math.round(v * 100)}% ${k}`);
    });
    Object.entries(advisor.penalties || {}).forEach(([k, v]) => {
      lines.push(`${Math.round(v * 100)}% ${k}`);
    });
    if (advisor.special) {
      lines.push(`⭐ ${advisor.special.description}`);
    }
    
    return lines.join('\n');
  };

  // Check if advisor is available (tech researched)
  const isAdvisorAvailable = (advisor) => {
    if (!advisor.unlockTech) return true;
    return researchedTechs.includes(advisor.unlockTech);
  };

  // Get advisor status
  const getAdvisorStatus = (advisor) => {
    if (activeAdvisors.includes(advisor.id)) return 'active';
    if (unlockedAdvisors.includes(advisor.id)) return 'unlocked';
    if (!isAdvisorAvailable(advisor)) return 'locked';
    return 'available';
  };

  // Check if can afford
  const canAfford = (cost) => {
    if (!cost) return true;
    return checkCanAfford(cost, playerResources);
  };

  // Group advisors by tier
  const advisorsByTier = useMemo(() => {
    const grouped = {};
    BALANCE.advisors.forEach(advisor => {
      const tier = advisor.tier || 1;
      if (!grouped[tier]) grouped[tier] = [];
      grouped[tier].push(advisor);
    });
    return grouped;
  }, []);

  // Filter advisors
  const filteredAdvisors = useMemo(() => {
    if (filterTier === 'all') return BALANCE.advisors;
    if (filterTier === 'available') {
      return BALANCE.advisors.filter(a => {
        const status = getAdvisorStatus(a);
        return status === 'available' || status === 'unlocked' || status === 'active';
      });
    }
    return BALANCE.advisors.filter(a => a.tier === parseInt(filterTier));
  }, [filterTier, researchedTechs, unlockedAdvisors, activeAdvisors]);

  // Get selected advisor info
  const selectedAdvisorInfo = useMemo(() => {
    if (!selectedAdvisor) return null;
    return {
      advisor: selectedAdvisor,
      status: getAdvisorStatus(selectedAdvisor),
      canAfford: canAfford(selectedAdvisor.baseCost),
    };
  }, [selectedAdvisor, unlockedAdvisors, activeAdvisors, playerResources, researchedTechs]);

  // Stats
  const totalAdvisors = BALANCE.advisors.length;
  const unlockedCount = unlockedAdvisors.length;
  const availableCount = BALANCE.advisors.filter(a => isAdvisorAvailable(a)).length;

  // Get next slot upgrade
  const nextSlotUpgrade = useMemo(() => {
    return BALANCE.advisorSlotUpgrades?.find(u => u.slots === maxSlots + 1);
  }, [maxSlots]);

  const canPurchaseSlot = useMemo(() => {
    if (!nextSlotUpgrade) return false;
    if (!canAfford(nextSlotUpgrade.cost)) return false;
    if (nextSlotUpgrade.unlockTech && !researchedTechs.includes(nextSlotUpgrade.unlockTech)) return false;
    return true;
  }, [nextSlotUpgrade, playerResources, researchedTechs]);

  return (
    <div className="modal-overlay adv-modal-overlay" onClick={onClose}>
      <div className="adv-modal" onClick={e => e.stopPropagation()}>
        
        {/* Header */}
        <header className="adv-modal-header">
          <div className="adv-header-left">
            <button className="adv-close-btn" onClick={onClose}>✕</button>
            <div className="adv-title">
              <span className="adv-title-icon">👑</span>
              <h2>Royal Advisors</h2>
            </div>
          </div>

          <div className="adv-header-center">
            <div className="adv-filter-group">
              <button 
                className={`adv-filter-btn ${filterTier === 'all' ? 'active' : ''}`}
                onClick={() => setFilterTier('all')}
              >
                📋 All
              </button>
              <button 
                className={`adv-filter-btn ${filterTier === 'available' ? 'active' : ''}`}
                onClick={() => setFilterTier('available')}
              >
                ✨ Available
              </button>
              {[1, 2, 3, 4, 5, 6].includes(parseInt(filterTier)) || (
                <select 
                  className="adv-tier-select"
                  value={filterTier}
                  onChange={(e) => setFilterTier(e.target.value)}
                >
                  <option value="all">By Era...</option>
                  {Object.keys(advisorsByTier).map(tier => (
                    <option key={tier} value={tier}>Era {tier}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="adv-header-right">
            <div className="adv-slots-display">
              <span className="adv-slots-icon">🪑</span>
              <span className="adv-slots-text">{activeAdvisors.length}/{maxSlots}</span>
            </div>
            <div className="adv-progress">
              <span className="adv-progress-label">{unlockedCount}/{availableCount} hired</span>
            </div>
          </div>
        </header>

        {/* Active Council Banner */}
        {activeAdvisors.length > 0 && (
          <div className="adv-council-banner">
            <span className="adv-council-label">Active Council:</span>
            <div className="adv-council-members">
              {activeAdvisors.map(advisorId => {
                const advisor = BALANCE.advisors.find(a => a.id === advisorId);
                if (!advisor) return null;
                return (
                  <div 
                    key={advisorId} 
                    className="adv-council-member"
                    onClick={() => setSelectedAdvisor(advisor)}
                    title={getAdvisorTooltip(advisor)}
                  >
                    <span className="adv-member-icon">{advisor.icon}</span>
                    <span className="adv-member-name">{advisor.name.split(' ')[0]}</span>
                    <button 
                      className="adv-member-dismiss"
                      onClick={(e) => {
                        e.stopPropagation();
                        deactivateAdvisor(advisorId);
                        onNotification?.(`${advisor.name} dismissed from council`, 'info');
                      }}
                    >
                      ✕
                    </button>
                  </div>
                );
              })}
              {activeAdvisors.length < maxSlots && (
                <div className="adv-council-empty">
                  <span>+</span>
                </div>
              )}
            </div>
            
            {/* Active Effects Summary */}
            <div className="adv-effects-summary">
              {(() => {
                const totals = getAdvisorTotalsForDisplay(activeAdvisors);

                return (
                  <>
                    {Object.entries(totals.bonuses).map(([k, v]) => (
                      <span key={k} className="adv-effect-chip bonus">
                        +{Math.round(v * 100)}% {k}
                      </span>
                    ))}
                    {Object.entries(totals.penalties).map(([k, v]) => (
                      <span key={k} className="adv-effect-chip penalty">
                        {Math.round(v * 100)}% {k}
                      </span>
                    ))}
                    {totals.specials.map((s, i) => (
                      <span key={i} className="adv-effect-chip special">
                        ⭐ {s.description}
                      </span>
                    ))}
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Main Content - Split View */}
        <div className="adv-split-view">
          
          {/* Advisor Grid (Left) */}
          <div className="adv-grid-panel">
            <div className="adv-grid">
              {filteredAdvisors.map(advisor => {
                const status = getAdvisorStatus(advisor);
                const isSelected = selectedAdvisor?.id === advisor.id;
                
                return (
                  <div
                    key={advisor.id}
                    className={`adv-card adv-card-${status} ${isSelected ? 'selected' : ''}`}
                    onClick={() => setSelectedAdvisor(advisor)}
                  >
                    {status === 'active' && <div className="adv-card-badge active">✓</div>}
                    {status === 'unlocked' && <div className="adv-card-badge unlocked">●</div>}
                    {status === 'locked' && <div className="adv-card-badge locked">🔒</div>}
                    
                    <div className="adv-card-icon">{advisor.icon}</div>
                    <div className="adv-card-name">{advisor.name.split(' ')[1] || advisor.name}</div>
                    <div className="adv-card-title">{advisor.title}</div>
                    
                    {status !== 'locked' && (
                      <div className="adv-card-preview">
                        {Object.entries(advisor.bonuses || {}).slice(0, 2).map(([k, v]) => (
                          <span key={k} className="adv-preview-bonus">
                            +{Math.round(v * 100)}%
                          </span>
                        ))}
                        {advisor.special && (
                          <span className="adv-preview-special">★</span>
                        )}
                      </div>
                    )}
                    
                    <div className="adv-card-tier">Era {advisor.tier}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Detail Panel (Right) */}
          <div className={`adv-detail-panel ${selectedAdvisorInfo ? 'expanded' : ''}`}>
            {selectedAdvisorInfo ? (
              <>
                <div className="adv-detail-header">
                  <span className="adv-detail-icon">{selectedAdvisorInfo.advisor.icon}</span>
                  <div className="adv-detail-titles">
                    <span className="adv-detail-name">{selectedAdvisorInfo.advisor.name}</span>
                    <span className="adv-detail-title">{selectedAdvisorInfo.advisor.title}</span>
                    <span className="adv-detail-era">Era {selectedAdvisorInfo.advisor.tier} • {TIER_NAMES[selectedAdvisorInfo.advisor.tier]}</span>
                  </div>
                  <span className={`adv-detail-status adv-status-${selectedAdvisorInfo.status}`}>
                    {selectedAdvisorInfo.status === 'active' && '✅ Active'}
                    {selectedAdvisorInfo.status === 'unlocked' && '📋 Hired'}
                    {selectedAdvisorInfo.status === 'available' && '✨ Available'}
                    {selectedAdvisorInfo.status === 'locked' && '🔒 Locked'}
                  </span>
                </div>

                <p className="adv-detail-desc">{selectedAdvisorInfo.advisor.description}</p>
                
                {selectedAdvisorInfo.advisor.flavor && (
                  <div className="adv-detail-flavor">{selectedAdvisorInfo.advisor.flavor}</div>
                )}

                {/* Bonuses */}
                {Object.keys(selectedAdvisorInfo.advisor.bonuses || {}).length > 0 && (
                  <div className="adv-detail-section">
                    <div className="adv-detail-label">📈 Bonuses</div>
                    <div className="adv-effects-list">
                      {Object.entries(selectedAdvisorInfo.advisor.bonuses).map(([key, val]) => (
                        <div key={key} className="adv-effect bonus">
                          <span className="adv-effect-icon">{resources[key]?.icon || '📊'}</span>
                          <span className="adv-effect-name">{key}</span>
                          <span className="adv-effect-value">+{Math.round(val * 100)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Penalties */}
                {Object.keys(selectedAdvisorInfo.advisor.penalties || {}).length > 0 && (
                  <div className="adv-detail-section">
                    <div className="adv-detail-label">📉 Drawbacks</div>
                    <div className="adv-effects-list">
                      {Object.entries(selectedAdvisorInfo.advisor.penalties).map(([key, val]) => (
                        <div key={key} className="adv-effect penalty">
                          <span className="adv-effect-icon">{resources[key]?.icon || '📊'}</span>
                          <span className="adv-effect-name">{key}</span>
                          <span className="adv-effect-value">{Math.round(val * 100)}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Special Ability */}
                {selectedAdvisorInfo.advisor.special && (
                  <div className="adv-detail-section special">
                    <div className="adv-detail-label">⭐ Special Ability</div>
                    <div className="adv-special-ability">
                      <span className="adv-special-desc">{selectedAdvisorInfo.advisor.special.description}</span>
                    </div>
                  </div>
                )}

                {/* Unlock Requirement */}
                {selectedAdvisorInfo.status === 'locked' && selectedAdvisorInfo.advisor.unlockTech && (
                  <div className="adv-detail-section requirement">
                    <div className="adv-detail-label">🔬 Requires Research</div>
                    <div className="adv-requirement">
                      <TechChip id={selectedAdvisorInfo.advisor.unlockTech} size="normal" />
                    </div>
                  </div>
                )}

                {/* Cost */}
                {selectedAdvisorInfo.status === 'available' && (
                  <div className="adv-detail-section">
                    <div className="adv-detail-label">💰 Hiring Cost</div>
                    <div className="adv-detail-costs">
                      {Object.entries(selectedAdvisorInfo.advisor.baseCost || {}).map(([r, a]) => (
                        <ResourceChip
                          key={r}
                          resourceId={r}
                          amount={a}
                          canAfford={(playerResources[r] || 0) >= a}
                          size="small"
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="adv-detail-actions">
                  {selectedAdvisorInfo.status === 'available' && (
                    <button
                      className="adv-action-btn hire"
                      disabled={!selectedAdvisorInfo.canAfford}
                      onClick={() => {
                        if (unlockAdvisor(selectedAdvisorInfo.advisor.id)) {
                          onNotification?.(`${selectedAdvisorInfo.advisor.name} has joined your court!`, 'success');
                        }
                      }}
                    >
                      {selectedAdvisorInfo.canAfford ? '👑 Hire Advisor' : '🔒 Cannot Afford'}
                    </button>
                  )}
                  
                  {selectedAdvisorInfo.status === 'unlocked' && (
                    <button
                      className="adv-action-btn activate"
                      disabled={activeAdvisors.length >= maxSlots}
                      onClick={() => {
                        if (activateAdvisor(selectedAdvisorInfo.advisor.id)) {
                          onNotification?.(`${selectedAdvisorInfo.advisor.name} joins your council!`, 'success');
                        }
                      }}
                    >
                      {activeAdvisors.length >= maxSlots ? '🪑 Council Full' : '✨ Add to Council'}
                    </button>
                  )}
                  
                  {selectedAdvisorInfo.status === 'active' && (
                    <button
                      className="adv-action-btn dismiss"
                      onClick={() => {
                        deactivateAdvisor(selectedAdvisorInfo.advisor.id);
                        onNotification?.(`${selectedAdvisorInfo.advisor.name} dismissed from council`, 'info');
                      }}
                    >
                      👋 Dismiss from Council
                    </button>
                  )}
                </div>
              </>
            ) : (
              <div className="adv-detail-empty">
                <span className="adv-empty-icon">👆</span>
                <span className="adv-empty-text">Select an advisor to view details</span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="adv-modal-footer">
          <div className="adv-legend">
            <span className="adv-legend-item"><span className="adv-legend-dot active" /> Active</span>
            <span className="adv-legend-item"><span className="adv-legend-dot unlocked" /> Hired</span>
            <span className="adv-legend-item"><span className="adv-legend-dot available" /> Available</span>
            <span className="adv-legend-item"><span className="adv-legend-dot locked" /> Locked</span>
          </div>

          {/* Slot Upgrade */}
          {nextSlotUpgrade && (
            <div className="adv-slot-upgrade">
              <span className="adv-slot-label">🪑 {nextSlotUpgrade.name}:</span>
              {nextSlotUpgrade.unlockTech && !researchedTechs.includes(nextSlotUpgrade.unlockTech) && (
                <span className="adv-slot-req">
                  🔬 {technologies[nextSlotUpgrade.unlockTech]?.name || nextSlotUpgrade.unlockTech}
                </span>
              )}
              <div className="adv-slot-cost">
                {Object.entries(nextSlotUpgrade.cost).map(([r, a]) => (
                  <ResourceChip
                    key={r}
                    resourceId={r}
                    amount={a}
                    canAfford={(playerResources[r] || 0) >= a}
                    size="small"
                  />
                ))}
              </div>
              <button 
                className="adv-slot-btn"
                disabled={!canPurchaseSlot}
                onClick={() => {
                  if (purchaseAdvisorSlot) {
                    const success = purchaseAdvisorSlot(nextSlotUpgrade);
                    if (success) {
                      onNotification?.(`Council expanded to ${nextSlotUpgrade.slots} seats!`, 'success');
                    }
                  }
                }}
              >
                Expand
              </button>
            </div>
          )}

          <div className="adv-hint">
            <kbd>Click</kbd> to select • Advisors provide bonuses and penalties
          </div>
        </footer>
      </div>
    </div>
  );
}

export default AdvisorsModal;
